<template>
    <modal :visible="visible">
        <template #header class="d-flex">
            <h2>{{ $t('fdb.subscriptions.change_miles' )}}</h2>
            <div class="close-icon" @click="$emit('hide')">
                X
            </div>
        </template>
        <template #body>
            <div>
                <p>
                    {{ $t('fdb.subscriptions.change_mileage_package_description') }}
                    <span v-if="showPriceWithoutTaxes">{{ $t('fdb.changeMileage.allPricesNet') }}</span>
                </p>
                <alert-box>
                    <div class="d-flex my-2">
                        <div>{{ $t('fdb.subscriptions.current_monthly') }}</div>
                        <div class="ml-auto"> 
                            <span v-if="showPriceWithoutTaxes">{{ $root.whitelabel.getFormatetPrice(subscription.price_plan.price_without_tax) }}</span>
                            <span v-else>{{ $root.whitelabel.getFormatetPrice(subscription.price_plan.price) }}</span>
                         </div>
                    </div>
                    <div class="d-flex my-2">
                        <div>{{ $t('fdb.subscriptions.current_mileage') }}</div>
                        <div class="ml-auto">{{ `${subscription.price_plan.mileage} ${$t('fdb.subscriptions.km_month')}` }}</div>
                    </div>
                </alert-box>
                <b-form-group class="my-4">
                    <b-form-select v-model="mileage" class="mb-3">
                        <b-form-select-option :value="null" disabled="">{{ $t('cdb.changeMileage.selectNewMileage') }}</b-form-select-option>
                        <b-form-select-option
                            v-for="pricePlan in subscription.vehicle.price_plans"
                            :key="pricePlan.price"
                            :value="pricePlan.mileage"
                        >
                        {{ $n(pricePlan.mileage, 'number') }} {{ $t('pds.buy_box.mileage_per_month') }}
                            <span v-if="showPriceWithoutTaxes">
                                {{ formatPrice(pricePlan.price_without_tax) }}
                            </span>
                            <span v-else>
                                {{ formatPrice(pricePlan.price) }}
                            </span>
                        </b-form-select-option>
                    </b-form-select>
                </b-form-group>
                <b-form-group class="my-4" v-if="mileage">
                    <b-form-radio v-model="terms" :value="true">
                        <span v-html="legalText"></span>
                    </b-form-radio>
                </b-form-group>
            </div>
        </template>
        <template #footer>
            <b-button :disabled="isButtonDisabled" @click="changeMileage" variant="danger" class="ml-auto">{{ $t('fdb.changeMileage.title') }}</b-button>
        </template>
    </modal>
</template>

<script>
import Modal from "../atoms/Modal.vue";
import IconPill from "../atoms/IconPill.vue";
import AlertBox from "../atoms/AlertBox.vue";
import { BButton, BFormGroup, BFormRadio, BFormSelect, BFormSelectOption } from "bootstrap-vue";
import moment from 'moment';

export default {
    name: 'SubscriptionChangeMileageModal',
    components: { Modal, IconPill, AlertBox, BFormGroup, BFormSelect, BFormSelectOption, BFormRadio, BButton },
    data() {
        return {
            mileage: null,
            terms: false,
            showPriceWithoutTaxes: this.$root.whitelabel.setting('show_price_without_taxes')
        }
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        subscription: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        isButtonDisabled() {
            return !this.terms || !this.mileage
        },
        getNewPrice() {
            const selectedMileage = this.subscription.vehicle.price_plans.find(pricePlan => pricePlan.mileage === this.mileage)
            return this.showPriceWithoutTaxes ? selectedMileage.price_without_tax : selectedMileage.price
        },
        getNewPriceForApi() {
            const selectedMileage = this.subscription.vehicle.price_plans.find(pricePlan => pricePlan.mileage === this.mileage)
            return selectedMileage.price
        },
        getBeginningOfNextPeriodDate() {
            const started = moment.unix(this.subscription.timestamps.started_at)
            const today = moment()
            const differenceInMonths = today.diff(started, 'months') + 1
            return this.$d(moment(started).add(differenceInMonths, 'M').toDate(), 'short')
        },
        getLegalTextLanguageVariable() {
            return this.showPriceWithoutTaxes ? 'fdb.changeMileage.legalTextNet' : 'fdb.changeMileage.legalText'
        },
         isMarketplaceOwner() {
             if(this.subscription.subsidiary_uuid === this.$root.whitelabel.sub_id()){
                return true
             }
             else {
                return false
             }
         },
        legalText() {
            if (this.$root.whitelabel.setting('type') == 'marketplace' && !this.isMarketplaceOwner){
                return this.$t(this.getLegalTextLanguageVariable + '.forWlMp', {
                    ownerGtc: `<a target="_blank" href=${this.$getUrl('content-page', { path: 'agb' })}>${this.$t('cdb.changeMileage.mpOwnerGtc')}</a>`,                    
                    participantGtc: `<a target="_blank" href=${this.$root.whitelabel.setting('customer_agb_file')}>${this.$t('cdb.changeMileage.mpParticipantGtc', {mp_participant: this.subscription.subsidiary_name})}</a>`,       
                    privacy: `<a target="_blank" href=${this.$getUrl('content-page', { path: 'datenschutz' })}>${this.$t('cdb.changeMileage.privacy')}</a>`,
                    beginningOfNextPeriod: this.getBeginningOfNextPeriodDate,
                    newPrice: this.$root.whitelabel.getFormatetPrice(this.getNewPrice)
                })
            } else {
                return this.$t(this.getLegalTextLanguageVariable, {
                    partnerGtc: `<a target="_blank" href=/agb>${this.$t('cdb.changeMileage.partnerGtc')}</a>`,
                    privacy: `<a target="_blank" href=/datenschutz>${this.$t('cdb.changeMileage.privacy')}</a>`,
                    beginningOfNextPeriod: this.getBeginningOfNextPeriodDate,
                    newPrice: `${this.$root.whitelabel.getFormatetPrice(this.getNewPrice)}`
                })
            }
        }
    },
    methods: {
        changeMileage() {
            return this.$emit('change-mileage', { mileage: this.mileage, price: this.getNewPriceForApi })
        },
        formatPrice(price) {
            return this.$root.whitelabel.getFormatetPrice(price)
        },
    }
}
</script>
